// Bingo builder glue: the preview variant arrows, the faction frequency-tier
// controls (translating a radio click into a hidden tier_override pin),
// free-space visibility, and add/remove for custom achievement rows. The tier
// table itself is rendered server-side and arrives via the htmx preview
// round-trip as an out-of-band swap, so this file never recomputes placement,
// regroups rows, or maintains pins across re-renders — it only records a
// deviation and asks for a fresh preview.

import { whenBuilderReady } from "./bingo-ready.js";

// The center / achievement / frequency-tier controls this binds live in the
// Advanced Settings body, which the static shell htmx-loads after first paint;
// wait for it. (The no-JS `/builder` page and the manage editor inline that
// body, so this binds at once there.)
whenBuilderReady(function init() {
  const form = document.getElementById("bingo-form");
  if (!form || !document.getElementById("bingo-free-list")) return false;

  // --- preview variant arrows ---------------------------------------------
  // The prev/next buttons flip through deterministic card variants of the
  // current settings: the hidden `preview_index` input carries the variant,
  // and a `preview-refresh` event makes htmx repost the form immediately (no
  // debounce). Setting the input programmatically fires no change event, so
  // flipping never counts as a settings change — while any *real* settings
  // change resets the history back to variant 0 before the debounced preview
  // fetch serializes the form.
  //
  // A variant is a pure function of (form state, index), so after every shown
  // card the next variant is prefetched into a one-slot cache and the next
  // arrow swaps it in without a round-trip. Any real settings change empties
  // the slot and bumps the generation so an in-flight prefetch discards its
  // (now mis-keyed) result.
  const previewEl = document.getElementById("bingo-preview");
  const previewIdx = document.getElementById("bingo-preview-index");
  const prevBtn = document.getElementById("bingo-preview-prev");
  const nextBtn = document.getElementById("bingo-preview-next");
  // The variant ceiling rides in on the hidden input's data-max, rendered from
  // the server's card cap — the client and the server clamp can't drift.
  const maxVariant = previewIdx ? parseInt(previewIdx.dataset.max, 10) || 0 : 0;
  // The slot is reserved (html: null) while its fetch is in flight, which
  // makes re-arming idempotent: the htmx:afterSwap listener and the explicit
  // re-arm on a cache hit can both fire for one swap without double-fetching.
  // setSlot is the only writer: it mirrors a *ready* slot's index onto the
  // preview element as data-prefetched, so the cache's state is observable
  // from outside this closure.
  let prefetched = null; // { idx, html | null }
  let prefetchGen = 0;
  function setSlot(slot) {
    prefetched = slot;
    if (slot && slot.html !== null)
      previewEl.dataset.prefetched = String(slot.idx);
    else delete previewEl.dataset.prefetched;
  }
  function prefetchNext() {
    const n = (parseInt(previewIdx.value, 10) || 0) + 1;
    if (n > maxVariant) return;
    if (prefetched && prefetched.idx === n) return;
    const slot = { idx: n, html: null };
    setSlot(slot);
    const gen = prefetchGen;
    const body = new URLSearchParams(new FormData(form));
    body.set("preview_index", String(n));
    fetch(previewEl.getAttribute("hx-post"), { method: "POST", body })
      .then((resp) =>
        resp.ok ? resp.text() : Promise.reject(new Error(String(resp.status))),
      )
      .then((html) => {
        if (gen === prefetchGen && prefetched === slot)
          setSlot({ idx: n, html });
      })
      .catch(() => {
        // Free the reservation so a later arm can retry; the arrow itself
        // just falls back to the live htmx fetch.
        if (gen === prefetchGen && prefetched === slot) setSlot(null);
      });
  }
  function gotoVariant(n) {
    previewIdx.value = String(n);
    prevBtn.disabled = n <= 0;
    nextBtn.disabled = n >= maxVariant;
    if (prefetched && prefetched.idx === n && prefetched.html !== null) {
      // htmx.swap applies the cached fragment exactly like a live response,
      // hx-swap-oob center labels and tier table included, and fires
      // htmx:afterSwap on the swapped-in nodes — but re-arm explicitly rather
      // than lean on that bubbling; prefetchNext dedupes if both happen.
      const html = prefetched.html;
      setSlot(null);
      htmx.swap(previewEl, html, { swapStyle: "innerHTML" });
      prefetchNext();
    } else {
      previewEl.dispatchEvent(new CustomEvent("preview-refresh"));
    }
  }
  if (previewEl && previewIdx && prevBtn && nextBtn) {
    nextBtn.addEventListener("click", () =>
      gotoVariant(
        Math.min((parseInt(previewIdx.value, 10) || 0) + 1, maxVariant),
      ),
    );
    prevBtn.addEventListener("click", () =>
      gotoVariant(Math.max((parseInt(previewIdx.value, 10) || 0) - 1, 0)),
    );
    form.addEventListener("change", () => {
      previewIdx.value = "0";
      prevBtn.disabled = true;
      nextBtn.disabled = false;
      prefetchGen++;
      setSlot(null);
    });
    // Live swaps (a cache miss or the debounced settings refresh) re-arm the
    // prefetch once they land; the first, server-baked preview never fires an
    // htmx event, so arm once at load too. The live preview also carries the
    // fresh tier table and center labels as out-of-band swaps, so there's
    // nothing else to re-derive here.
    previewEl.addEventListener("htmx:afterSwap", () => {
      prefetchNext();
    });
    prefetchNext();
  }

  // --- faction frequency tiers --------------------------------------------
  // #bingo-tier-body is rendered server-side and swapped in out-of-band on
  // every preview, so it always shows the current placement: each faction's
  // radio checked at its effective tier, regrouped season/reason headers, and a
  // hidden tier_override input per deviation. The only client work is turning a
  // radio click into that hidden input and asking for an immediate refresh.
  //
  // Because the body is replaced wholesale on each swap, controls inside it
  // can't carry their own listeners (a swap would drop them) — delegate from
  // the stable form instead.

  // Skip the 600ms settings debounce: a click on a tier control should reflect
  // at once. The form `change` still bubbles and would refresh on its own
  // delay, but preview-refresh makes htmx repost now.
  function refreshPreview() {
    if (previewEl) previewEl.dispatchEvent(new CustomEvent("preview-refresh"));
  }

  // Selecting a frequency pins the row to that tier — unless the choice lands
  // back on the row's automatic tier (data-auto), which releases the pin so
  // only genuine deviations persist. The hidden tier_override input is the
  // source of truth the server reads (valued `tier:faction`, the format the
  // server parses); the display radios are ignored for pin purposes. The
  // server recomputes auto each render and drops any pin that equals it, so a
  // stale pin can't survive a scope/preset change.
  function pinFromRadio(radio) {
    const row = radio.closest(".bingo-tier-row");
    if (!row || radio.disabled) return;
    const pin = row.querySelector(".bingo-tier-pin");
    if (!pin) return;
    pin.querySelector('input[name="tier_override"]')?.remove();
    if (radio.value !== row.dataset.auto) {
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "tier_override";
      hidden.value = `${radio.value}:${row.dataset.faction}`;
      pin.appendChild(hidden);
    }
    refreshPreview();
  }

  // Frequency radios are swapped in with the table body, so they're reached by
  // delegation. The scope and preset radios are stable chrome, but route
  // through the same handler so "a tier control changed → refresh now" lives in
  // one place.
  form.addEventListener("change", (e) => {
    const t = e.target;
    if (t.classList.contains("bingo-freq")) {
      pinFromRadio(t);
    } else if (
      t.classList.contains("bingo-scope") ||
      t.classList.contains("bingo-preset")
    ) {
      refreshPreview();
    }
  });

  // Release every pin back to automatic placement, then refresh. The button
  // sits outside the swapped body, so it keeps its listener; clearing the
  // hidden inputs is enough, since the server recomputes auto and re-renders.
  document
    .getElementById("bingo-reset-overrides")
    ?.addEventListener("click", () => {
      document
        .querySelectorAll('#bingo-tier-body input[name="tier_override"]')
        .forEach((input) => input.remove());
      refreshPreview();
    });

  // --- achievement select all / none (covers archetype chips too) --------
  function setAchievements(checked) {
    document.querySelectorAll(".bingo-achievement").forEach((b) => {
      b.checked = checked;
    });
    form.dispatchEvent(new Event("change", { bubbles: true }));
  }
  document
    .getElementById("bingo-ach-all")
    ?.addEventListener("click", () => setAchievements(true));
  document
    .getElementById("bingo-ach-none")
    ?.addEventListener("click", () => setAchievements(false));

  // --- center square (always reserved on every grid) ---------------------
  // A `center_mode` radio group picks the center mode; the checked radio posts
  // with the form on its own. The client shows the editable label field only
  // when FREE is selected, and refreshes the preview at once — the server
  // re-resolves the center faction (it leaves the rest of the board) and ships
  // back the regrouped tier table out-of-band.
  const centerLabelWrap = document.getElementById("bingo-free-label-wrap");
  function syncCenter() {
    const checked = document.querySelector(".bingo-center:checked");
    const mode = checked ? checked.value : "free";
    if (centerLabelWrap)
      centerLabelWrap.style.display = mode === "free" ? "" : "none";
  }
  document.querySelectorAll(".bingo-center").forEach((radio) => {
    radio.addEventListener("change", () => {
      syncCenter();
      refreshPreview();
    });
  });
  syncCenter();

  // --- custom achievement rows -------------------------------------------
  const list = document.getElementById("bingo-free-list");
  const addBtn = document.getElementById("bingo-add-free");

  function blankRow() {
    const row = document.createElement("div");
    row.className = "bingo-free-row flex items-center gap-2";
    row.innerHTML =
      '<input type="text" name="achievement_free" maxlength="120" placeholder="Type a custom square…" ' +
      'class="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-beanie-orange">' +
      '<button type="button" class="bingo-free-remove text-gray-400 hover:text-red-500 text-sm">&times;</button>';
    return row;
  }

  if (addBtn && list) {
    addBtn.addEventListener("click", () => {
      list.appendChild(blankRow());
    });
  }

  if (list) {
    list.addEventListener("click", (e) => {
      const btn = e.target.closest(".bingo-free-remove");
      if (!btn) return;
      const rows = list.querySelectorAll(".bingo-free-row");
      const row = btn.closest(".bingo-free-row");
      if (rows.length > 1) {
        row.remove();
      } else {
        // Keep at least one row — just clear it.
        const input = row.querySelector('input[name="achievement_free"]');
        if (input) input.value = "";
      }
      form.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  return true;
});
